import { EMPTY, of } from 'rxjs';
import { mergeMap, map, catchError } from 'rxjs/operators';
import { ofType } from 'redux-observable';

import React from 'react';
import moment from 'moment';
import { defineMessages, FormattedMessage } from 'react-intl';

import { ModalService } from 'ui-components/services/modal-service';
import { ErrorModal } from '../components/modals/error-modal';
import { AddressVerificationStatusMap } from '../enums/address-verification-status';
import * as Api from './address.api';

import Global from '../store/global-selectors';
import { updateSalesOrderShipToAndSoldTo, updateSalesOrderShipTo } from '../orders/actions';
import * as Actions from './actions';
import * as shipmentDetailsAction from '../store/shipments/shipment-details/shipments.details.actions';
import { AU } from '../constants/country-codes';

export const validateAddressOrderDetails = (action$) =>
  action$.pipe(
    ofType(Actions.VALIDATE_ADDRESS_SALES_ORDER_SHIP_TO_REQUEST),
    mergeMap((action) => {
      return Api.validateAddress(action.address, action.email).pipe(
        map((response) =>
          Actions.validateAddressSalesOrderShipToSuccess(
            response,
            action.email,
            action.address,
            action.salesOrderId,
            action.fulfillmentPlanId,
          ),
        ),
        catchError((err) => of(Actions.validateAddressSalesOrderShipToFailure(err))),
      );
    }),
  );

const validateMessages = defineMessages({
  addressValidationFailureTitle: {
    description: 'orderDetailsEpics.addressValidationFailureTitle',
    defaultMessage: 'Address Validation Error',
    id: '6IdvRg',
  },
  addressValidationFailureMessage: {
    description: 'orderDetailsEpics.addressValidationFailureMessage',
    defaultMessage: 'This address cannot be validated at this time. Please check the address and try again.',
    id: 'caHM0B',
  },
});
export const updateOrderAfterAddressValidation = (action$, { getState }) =>
  action$.pipe(
    ofType(Actions.VALIDATE_ADDRESS_SALES_ORDER_SHIP_TO_SUCCESS),
    mergeMap(({ addressValidationResponses, email, address, salesOrderId, fulfillmentPlanId }) => {
      const addressValidationVerifiedStatus = 'Verified';

      const state = getState();
      const currentShipTo =
        Global.selectEntitiesSalesOrderProperty(state, {
          salesOrderId,
          property: 'shipTos.0',
        }) || {};
      const addressValidationResponse = addressValidationResponses[0];
      const originalCountryCode = addressValidationResponse.originalAddress.countryCode;
      const correctValidationStatus = addressValidationResponse.status === addressValidationVerifiedStatus;
      let matchedAddressAppearsValid = addressValidationResponse?.matchedAddress?.line1 !== '';

      // SPD-15442, we want to test only specific non-US countries for safety (and for sure don't touch US)
      const countryCodesToSkipAddressOverwriting = [AU];

      if (countryCodesToSkipAddressOverwriting.includes(originalCountryCode)) {
        matchedAddressAppearsValid = correctValidationStatus;
      }

      const addressToUseInCleanShipTo = matchedAddressAppearsValid ? addressValidationResponse.matchedAddress : {};

      const { phone, name, lockAddress } = address;

      const cleansedShipToAddress = {
        ...currentShipTo,
        ...addressValidationResponse.originalAddress,
        ...addressToUseInCleanShipTo,
        phone,
        name,
        verificationStatus: AddressVerificationStatusMap[addressValidationResponse.status],
        verificationMessage: addressValidationResponse.messages?.[0] || '',
        verificationUtc: moment.utc().format(),
        lockAddress,
      };

      // If we don't have an email (like when validating from the Orders grid), we only need to update the Sales Order Ship To.
      // If an email is present, this is being called as part of editing an order's address, so we need to update the Sales Order
      // Ship To & Sold To both. - greg s. 12/6/19
      const shouldUpdateSalesOrderSoldTo = !!email;

      if (!shouldUpdateSalesOrderSoldTo) {
        return of(
          updateSalesOrderShipTo({
            salesOrderId,
            address: cleansedShipToAddress,
            fulfillmentPlanId,
          }),
        );
      }

      return of(
        updateSalesOrderShipToAndSoldTo({
          salesOrderId,
          address: cleansedShipToAddress,
          email,
          fulfillmentPlanId,
        }),
      );
    }),
  );

export const validateAddressSalesOrderShipToFailure = (action$) =>
  action$.pipe(
    ofType(Actions.VALIDATE_ADDRESS_SALES_ORDER_SHIP_TO_FAILURE, shipmentDetailsAction.VALIDATE_ADDRESS_SHIPMENT_DETAILS_SHIPTO_FAILURE),
    mergeMap(() => {
      ModalService.launchModal({
        component: ErrorModal,
        props: {
          title: <FormattedMessage {...validateMessages.addressValidationFailureTitle} />,
          message: <FormattedMessage {...validateMessages.addressValidationFailureMessage} />,
        },
      });
      return EMPTY;
    }),
  );
